import { memo, type RefObject, useMemo } from "react";
import AiAssistantChatContext from "../AiAssistantChatContext/AiAssistantChatContext.tsx";
import { parsePromptContext } from "../AiAssistantChatContext/AiAssistantChatContext.utils.ts";
import AiAssistantPromptsSuggestions from "../AiAssistantPromptsSuggestions/AiAssistantPromptsSuggestions.tsx";
import MessageBubble from "../MessageBubble/MessageBubble";
import TypingIndicator from "../TypingIndicator/TypingIndicator";
import {
  useAiAssistantMessages,
  useAiAssistantMessagesScroll,
} from "./AiAssistantMessages.hooks";
import type {
  AiAssistantMessagesProps,
  Messages,
} from "./AiAssistantMessages.types";

type MessageRowProps = {
  msg: Messages[number];
  isLast: boolean;
  isLoading: boolean;
  showRetryButton: boolean;
  isEditing: boolean;
  isLastUserMsg: boolean;
  contextChanged: boolean;
  lastUserMsgRef: RefObject<HTMLDivElement | null>;
  retry: (messageId: string) => void;
  retryLast: () => void;
  switchMessageBranch: (messageId: string, delta: -1 | 1) => void;
  startEditMessage: (messageId: string) => void;
  submitEditMessage: (messageId: string, newContent: string) => void;
  cancelEditMessage: () => void;
};

// Memoized so completed message rows do not re-render on every streaming tick.
// All upstream callbacks come from `useChatStream` and are `useCallback`-stable;
// inline closures bound to `msg.id` are created here so they only refresh when
// this row's props actually change.
const AiAssistantMessageRow = memo(function AiAssistantMessageRow({
  msg,
  isLast,
  isLoading,
  showRetryButton,
  isEditing,
  isLastUserMsg,
  contextChanged,
  lastUserMsgRef,
  retry,
  retryLast,
  switchMessageBranch,
  startEditMessage,
  submitEditMessage,
  cancelEditMessage,
}: MessageRowProps) {
  const parsedPromptContext = useMemo(
    () => parsePromptContext(msg.context),
    [msg.context]
  );

  const handleRetry =
    msg.type === "ai" && msg.id ? () => retry(msg.id as string) : retryLast;
  const handleStartEdit = msg.id
    ? () => startEditMessage(msg.id as string)
    : undefined;
  const handleSubmitEdit = msg.id
    ? (newContent: string) => submitEditMessage(msg.id as string, newContent)
    : undefined;
  const handleSwitchBranch =
    msg.id && msg.branchInfo
      ? (delta: -1 | 1) => switchMessageBranch(msg.id as string, delta)
      : undefined;

  return (
    <>
      {contextChanged && (
        <div className="-mb-2">
          <AiAssistantChatContext
            entries={parsedPromptContext}
            url={msg.contextUrl}
          />
        </div>
      )}
      <div ref={isLastUserMsg ? lastUserMsgRef : undefined}>
        <MessageBubble
          content={msg.content}
          type={msg.type}
          isLast={isLast}
          isLoading={isLoading}
          onRetry={handleRetry}
          showRetryButton={showRetryButton}
          branchInfo={msg.branchInfo}
          onSwitchBranch={handleSwitchBranch}
          isEditing={isEditing}
          onStartEdit={handleStartEdit}
          onSubmitEdit={handleSubmitEdit}
          onCancelEdit={cancelEditMessage}
          data-test-id="ai-toolbox-message"
        />
      </div>
    </>
  );
});

export default function AiAssistantMessages({
  messages,
  streamingContent,
  isLoading,
  isThreadLoading,
  retry,
  retryLast,
  switchMessageBranch,
  editingMessageId,
  startEditMessage,
  submitEditMessage,
  cancelEditMessage,
  send,
}: AiAssistantMessagesProps) {
  const { scrollRef, lastUserMsgRef, spacerHeight } = useAiAssistantMessagesScroll(
    messages,
    streamingContent
  );

  const hasMessages = messages.length > 0 || streamingContent;

  const showEmptyState = !hasMessages && !isThreadLoading;

  const { lastHumanIdx, hasFollowingAiAfter, contextChanged } =
    useAiAssistantMessages(messages);

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto overflow-x-visible -mr-7 -ml-6 pr-5 pl-6 py-4 min-h-0 scrollShadow"
    >
      {!hasMessages ? (
        showEmptyState && (
          <div className="flex flex-col items-center justify-center h-full text-text-secondary text-sm">
            <AiAssistantPromptsSuggestions onSubmit={send} isLoading={isLoading} />
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3">
          {messages.map((msg, idx) => {
            const isHumanPrompt = msg.type === "human";
            const isLastUserMsg = isHumanPrompt && idx === lastHumanIdx;
            const hasFollowingAiMsg = isHumanPrompt && hasFollowingAiAfter[idx];
            const showRetryButton =
              msg.type === "ai" || (isHumanPrompt && !hasFollowingAiMsg);
            const isLast =
              !streamingContent && !isLoading && idx === messages.length - 1;

            return (
              <AiAssistantMessageRow
                key={msg.id}
                msg={msg}
                isLast={isLast}
                isLoading={isLoading}
                showRetryButton={showRetryButton}
                isEditing={editingMessageId === msg.id}
                isLastUserMsg={isLastUserMsg}
                contextChanged={contextChanged[idx]}
                lastUserMsgRef={lastUserMsgRef}
                retry={retry}
                retryLast={retryLast}
                switchMessageBranch={switchMessageBranch}
                startEditMessage={startEditMessage}
                submitEditMessage={submitEditMessage}
                cancelEditMessage={cancelEditMessage}
              />
            );
          })}
          {streamingContent && (
            <div style={{ minHeight: spacerHeight }}>
              <MessageBubble
                content={streamingContent}
                isStreaming
                data-test-id="ai-toolbox-message-streaming"
              />
            </div>
          )}
          {isLoading && !streamingContent && (
            <div style={{ minHeight: spacerHeight }}>
              <TypingIndicator />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
